import React, { useMemo, useCallback } from 'react'
import { useTable, useFlexLayout, useFilters, useSortBy } from 'react-table'
import { FaTimes } from 'react-icons/fa'
import { useState } from 'react'
import ConfirmationModal from '../../../../../features/reporting/ConfirmationModal'
import SearchField from '../../table/SearchField'
import FilterOperatorSelect from '../../../../filters/FilterOperatorSelect'
import {
    operatorFilterFunction,
    getDefaultOperatorForType,
} from '../../../../../../utils/filters/filterOperators'

import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import onRowSelect from './onRowSelect'
import './assignmentMenu.scss'
import DraggableTableRow, {
    DragHandle,
    StaticTableRow,
} from './DraggableTableRow'
import styled from 'styled-components'
import { useRef } from 'react'
import { useEffect } from 'react'

const CenteredHead = styled.div`
    flex: none;
    width: 100%;
    text-align: center;
`
const TableWrapper = styled.div`
    overflow: auto; //our scrollable table container
    position: relative; //needed for sticky header
    height: ${(props) =>
        props.tableHeight
            ? props.tableHeight
            : '100vh'}; //should be a fixed height
`
// play around with the tableHeight prop to fit your menu

//all the scroll bar code is taken from this example https://tanstack.com/table/v8/docs/framework/react/examples/virtualized-infinite-scrolling?panel=sandbox
//scrollbars with sticky headers in react table have proven to be very hard to implement always
//and very specific styling props have to be added to accomplish it
//that example has been the easiest way to do so in my experience,

const DefaultColumnFilter = ({ column: { filterValue, preFilteredRows, setFilter, fieldType } }) => {
    const inputRef = useRef(null)
    const count = preFilteredRows.length
    const initOperator =
        filterValue && typeof filterValue === 'object'
            ? filterValue.operator
            : getDefaultOperatorForType(fieldType)
    const initValue =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value || ''
            : filterValue || ''
    const initValue2 =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value2 || ''
            : ''

    const [operator, setOperator] = useState(initOperator)
    const [value, setValue] = useState(initValue)
    const [value2, setValue2] = useState(initValue2)

    const updateFilter = useCallback(
        (op, v1, v2) => {
            if (op !== 'BLANK' && !v1 && v1 !== 0) {
                setFilter(undefined)
                return
            }
            setFilter({
                operator: op,
                value: v1,
                value2: v2 || undefined,
                fieldType: fieldType || 'string',
            })
        },
        [setFilter, fieldType]
    )

    const clearFilter = useCallback(() => {
        if (inputRef.current) inputRef.current.value = null
        setValue('')
        setValue2('')
        setFilter(undefined)
    }, [setFilter, fieldType])

    const onOperatorChange = useCallback(
        (op) => {
            setOperator(op)
            if (op === 'BLANK') {
                setValue('')
                setValue2('')
                updateFilter('BLANK', '', '')
                return
            }
            const newValue2 = op === 'BETWEEN' ? value2 : ''
            if (op !== 'BETWEEN') setValue2('')
            updateFilter(op, value, newValue2)
        },
        [value, value2, updateFilter]
    )

    const onValueChange = useCallback(
        (e) => {
            const v = e.target.value
            setValue(v)
            updateFilter(operator, v, value2)
        },
        [operator, value2, updateFilter]
    )

    const onValue2Change = useCallback(
        (e) => {
            const v = e.target.value
            setValue2(v)
            updateFilter(operator, value, v)
        },
        [operator, value, updateFilter]
    )

    useEffect(() => {
        if (!filterValue) {
            setValue('')
            setValue2('')
        }
    }, [filterValue, fieldType])

    return (
        <div className="tableFilterWrapper">
            <FilterOperatorSelect
                fieldType={fieldType || 'string'}
                value={operator}
                onChange={onOperatorChange}
            />
            <div className="control is-expanded has-icons-right">
                <input
                    ref={inputRef}
                    className="input is-small is-size-7"
                    value={value}
                    onChange={onValueChange}
                    disabled={operator === 'BLANK'}
                    placeholder={`Filter...`}
                />
                <span
                    className="icon is-right"
                    style={{ pointerEvents: 'auto', cursor: 'pointer', color: '#9e9e9e' }}
                    onClick={clearFilter}
                >
                    <FaTimes size={12} />
                </span>
            </div>
            {operator === 'BETWEEN' && (
                <input
                    value={value2}
                    onChange={onValue2Change}
                    className="input is-small is-size-7 filterBetweenInput"
                    placeholder="End value..."
                />
            )}
        </div>
    )
}

const MoveToggleLabel = ({ onMoveToggle, formProps }) => {
    const [moveEnabled, setMoveEnabled] = useState(false)
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    return (
        <>
            {onMoveToggle && formProps ? (
                <span
                    className="legendButton"
                    onClick={(e) => {
                        onMoveToggle(
                            moveEnabled,
                            setMoveEnabled,
                            setShowConfirmationModal
                        )
                    }}
                >
                    {`Reorder Columns`}
                    {/* {moveEnabled
                        ? `  Column Reorder`
                        : `  Column Reorder`
                    } */}
                </span>
            ) : null}
            <ConfirmationModal
                display={showConfirmationModal}
                title={'Unsaved Changes'}
                confirmAction={() => {
                    if (formProps.resetForm) {
                        formProps.resetForm()
                    }
                }}
                confirmText={`Discard Changes`}
                denyAction={() => setShowConfirmationModal(false)}
                denyText={`Cancel`}
            >
                <p>
                    There are unsaved changes. Do you wish to discard these
                    changes?
                </p>
            </ConfirmationModal>
        </>
    )
}

const getActionColumn = (onDelete, rowSelector, viewOnly) => {
    let actions = []
    if (onDelete !== null) {
        actions = [
            ...actions,
            {
                Header: '',
                accessor: 'actions',
                width: 45,
                Cell: ({
                    cell,
                    value: initialValue,
                    row: { index, original },
                    column: { id, accessor },
                    highlightedRows,
                }) => {
                    // const isHighlighted = highlightedRows.find(x => x[rowSelector] === original[rowSelector])
                    return onDelete !== null ? (
                        <>
                            <div
                                className={`delete-button`} // ${isHighlighted ? 'isHighlightedTableDeleteButton' : ''}`}
                                onClick={() => {
                                    onDelete(index)
                                }}
                            >
                                <FaTimes />
                            </div>
                        </>
                    ) : null
                },
                disableSortBy: true,
                disableFilters: true,
            },
        ]
    }
    return actions
}

const dragHandleColumn = {
    Header: '',
    accessor: 'dragHandle',
    width: 40,
    Cell: (props) => <DragHandle {...props.attributes} {...props.listeners} />,
    disableSortBy: true,
    disableFilters: true,
}

const defaultColumn = {
    minWidth: 30, // minWidth is only used as a limit for resizing
    // width: 175, // width is used for both the flex-basis and flex-grow
    maxWidth: 300, // maxWidth is only used as a limit for resizing
    Filter: DefaultColumnFilter,
    filter: operatorFilterFunction,
}

const GenericTable = ({
    rowSelector = 'id', //unique key for the row
    tableHeight,
    columns,
    fields,
    tableTile,
    viewOnly,
    filterable = false,
    onHightlight = null, //pass a function if action should happen on highlight or true if it just needs to be highlightable
    highlightedRows = [],
    onAddProps = null,
    onDelete = null,
    onMove = null,
    onMoveToggle = null, //if you don't define onMoveToggle, move will always be enable
    formProps = null,
    underRowHeaderName,
    noDataMessage = 'No data to display',
    onFilteredRowsChange = null,
}) => {
    const [activeId, setActiveId] = useState()
    const uniqueItems = useMemo(() => fields?.map(({ id }) => id), [fields])
    const isHighlightable = useMemo(() => onHightlight !== null, [onHightlight])
    const [lastSelection, setLastSelection] = useState(null)

    const noData = useMemo(
        () => !fields || (fields && fields.length <= 0),
        [fields]
    )

    const finalColumnList = useMemo(() => {
        let finalColumns = !!columns ? columns : []
        if (finalColumns) {
            const actionColumn = getActionColumn(
                onDelete,
                rowSelector,
                viewOnly
            )
            if (actionColumn !== null && actionColumn.length) {
                finalColumns = [...finalColumns, ...actionColumn]
            }
            if (onMove !== null) {
                finalColumns = [dragHandleColumn, ...finalColumns]
            }
        }
        return finalColumns
    }, [columns, viewOnly, onDelete, rowSelector, onMove])

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
        useTable(
            {
                columns: finalColumnList,
                data: fields,
                defaultColumn,
                initialState: {
                    hiddenColumns: [viewOnly ? 'actions' : ''],
                },
                highlightedRows,
            },
            useFilters,
            useSortBy,
            useFlexLayout
        )

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    )

    const handleDragStart = (e) => {
        setActiveId(e.active.id)
    }

    const handleDragEnd = (e) => {
        const { active, over } = e
        if (active.id !== over.id && onMove !== null) {
            const oldIndex = uniqueItems.indexOf(active.id)
            const newIndex = uniqueItems.indexOf(over.id)
            onMove(oldIndex, newIndex)
        }
        setActiveId(null)
    }

    function handleDragCancel() {
        setActiveId(null)
    }

    const selectedRow = useMemo(() => {
        if (!activeId) {
            return null
        }
        const row = rows.find(
            ({ original }) => original[rowSelector] === activeId
        )
        prepareRow(row)
        return row
    }, [activeId, rows, prepareRow, rowSelector])

    const onRowClick = useCallback(
        (e, currentRow, allRows) => {
            // e.stopPropagation()
            if (e.target.localName === 'td' || e.target.localName === 'span') {
                //only trigger event if target is a td element https://javascript.info/bubbling-and-capturing
                if (isHighlightable) {
                    onRowSelect(
                        rowSelector,
                        highlightedRows,
                        onHightlight,
                        lastSelection,
                        setLastSelection,
                        e,
                        currentRow,
                        allRows,
                        fields
                    )
                    // onHightlight(highlightedRows)
                }
            }
        },
        [
            isHighlightable,
            rowSelector,
            highlightedRows,
            onHightlight,
            lastSelection,
            setLastSelection,
            fields,
            onHightlight,
        ]
    )

    // useEffect(() => { // if highlighted rows change, execute action
    //     if (isHighlightable) {
    //         onHightlight(highlightedRows)
    //     }
    // }, [highlightedRows, onHightlight, isHighlightable])

    useEffect(() => {
        if (onFilteredRowsChange) {
            const filteredRows = rows.map((r) => r.original)
            onFilteredRowsChange({
                filteredRows,
                filteredCount: filteredRows.length,
            })
        }
    }, [rows, onFilteredRowsChange])

    const checkIfIsHighlighted = useCallback(
        (currentRow) => {
            return !!highlightedRows.find(
                (x) => x[rowSelector] === currentRow.original[rowSelector]
            )
        },
        [highlightedRows, rowSelector]
    )

    return (
        <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
        >
            <CenteredHead>
                <h3>{tableTile}</h3>
                <span>
                    {noData ? (
                        noDataMessage
                    ) : (
                        <>
                            {`Displaying ${rows.length} of ${fields.length} total records`}
                            <MoveToggleLabel
                                onMoveToggle={onMoveToggle}
                                formProps={formProps}
                            />
                        </>
                    )}
                </span>
            </CenteredHead>
            {onAddProps !== null && (
                <div>
                    <button
                        type="button"
                        className="button is-info"
                        style={{ marginBottom: '15px' }}
                        onClick={onAddProps.onClick}
                        disabled={onAddProps.disabled}
                    >
                        {onAddProps.text || `+ Add Column`}
                    </button>
                </div>
            )}
            <TableWrapper
                tableHeight={tableHeight}
                className="gridWrapperScrollBar"
            >
                <table
                    {...getTableProps()}
                    style={{ display: noData ? 'none' : 'grid' }}
                >
                    <thead
                        style={{
                            display: 'grid',
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                            backgroundColor: 'white',
                        }}
                    >
                        {headerGroups.map((headerGroup) => (
                            <tr
                                {...headerGroup.getHeaderGroupProps()}
                                style={{ display: 'flex', width: '100%' }}
                            >
                                {headerGroup.headers
                                    .filter(
                                        (x) => x.Header !== underRowHeaderName
                                    )
                                    .map((column) => (
                                        <th {...column.getHeaderProps()}>
                                            {column.render('Header')}
                                            {filterable ? (
                                                <div className="filter">
                                                    {column.canFilter
                                                        ? column.render(
                                                              'Filter'
                                                          )
                                                        : null}
                                                </div>
                                            ) : null}
                                        </th>
                                    ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        <SortableContext
                            items={uniqueItems}
                            strategy={verticalListSortingStrategy}
                        >
                            {rows.map((row) => {
                                const rowId = row.original[rowSelector]
                                const isHighlighted = checkIfIsHighlighted(row)
                                prepareRow(row)
                                return (
                                    <DraggableTableRow
                                        key={rowId}
                                        row={row}
                                        rows={rows}
                                        rowId={rowId}
                                        onMove={onMove}
                                        isHighlighted={isHighlighted}
                                        onRowClick={onRowClick}
                                        underRowHeaderName={underRowHeaderName}
                                    />
                                )
                            })}
                        </SortableContext>
                    </tbody>
                </table>
                <DragOverlay>
                    {activeId && (
                        <table style={{ width: '100%' }}>
                            <tbody>
                                <StaticTableRow
                                    row={selectedRow}
                                    isHighlighted={checkIfIsHighlighted(
                                        selectedRow
                                    )}
                                />
                            </tbody>
                        </table>
                    )}
                </DragOverlay>
            </TableWrapper>
        </DndContext>
    )
}

export default GenericTable
